import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = {
  employee_id: "",
  name: "",
  password: "",
  role: "recepcionista",
  center_id: "",
};

const emptyEdit = {
  id: "",
  employee_id: "",
  name: "",
  password: "",
  role: "recepcionista",
  center_id: "",
};

const ROLES_LABEL = {
  admin: "Administrador (jefe)",
  center_admin: "Admin de centro",
  gerente: "Gerente",
  recepcionista: "Recepcionista",
  audioprotesista: "Audioprotesista",
};

const WEEK_DAYS = [
  ["mon", "Lunes"],
  ["tue", "Martes"],
  ["wed", "Miércoles"],
  ["thu", "Jueves"],
  ["fri", "Viernes"],
  ["sat", "Sábado"],
  ["sun", "Domingo"],
];

const emptySchedule = () =>
  Object.fromEntries(WEEK_DAYS.map(([k]) => [k, { enabled: false, s1: "", e1: "", s2: "", e2: "" }]));

const scheduleFromApi = (ws) => {
  const out = emptySchedule();
  if (ws && typeof ws === "object") {
    WEEK_DAYS.forEach(([k]) => {
      const ranges = Array.isArray(ws[k]) ? ws[k] : [];
      if (ranges.length > 0) {
        out[k] = {
          enabled: true,
          s1: ranges[0]?.start || "",
          e1: ranges[0]?.end || "",
          s2: ranges[1]?.start || "",
          e2: ranges[1]?.end || "",
        };
      }
    });
  }
  return out;
};

const scheduleToApi = (sched) => {
  const out = {};
  WEEK_DAYS.forEach(([k]) => {
    const d = sched[k] || {};
    const ranges = [];
    if (d.enabled) {
      if (d.s1 && d.e1 && d.s1 < d.e1) ranges.push({ start: d.s1, end: d.e1 });
      if (d.s2 && d.e2 && d.s2 < d.e2) ranges.push({ start: d.s2, end: d.e2 });
    }
    out[k] = ranges;
  });
  return out;
};

export default function Users() {
  const { centers, user: currentUser } = useAuth();
  const isAdmin = ["admin", "center_admin"].includes(currentUser?.role);
  const isGlobalAdmin = currentUser?.role === "admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Edición
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [editSaving, setEditSaving] = useState(false);
  const [editSchedule, setEditSchedule] = useState(emptySchedule());

  const load = () => api.get("/users").then((r) => setUsers(r.data));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", {
        ...form,
        center_id: form.role === "admin" ? null : form.center_id || null,
      });
      toast.success("Usuario creado");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar usuario?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const openEdit = (u) => {
    setEditForm({
      id: u.id,
      employee_id: u.employee_id || "",
      name: u.name || "",
      password: "",
      role: u.role || "recepcionista",
      center_id: u.center_id || "",
    });
    setEditSchedule(scheduleFromApi(u.work_schedule));
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const payload = {
        employee_id: editForm.employee_id,
        name: editForm.name,
        role: editForm.role,
        center_id: editForm.role === "admin" ? null : (editForm.center_id || null),
        work_schedule: scheduleToApi(editSchedule),
      };
      if (editForm.password) {
        payload.password = editForm.password;
      }
      await api.patch(`/users/${editForm.id}`, payload);
      toast.success("Trabajador actualizado");
      setEditOpen(false);
      setEditForm(emptyEdit);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Administración
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Trabajadores
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white">
              <Plus className="h-4 w-4 mr-2" /> Nuevo trabajador
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Nuevo trabajador</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>ID Trabajador *</Label>
                <Input
                  data-testid="user-employee-id"
                  required
                  value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                />
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Contraseña *</Label>
                <Input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isGlobalAdmin && <SelectItem value="admin">Administrador general (jefe)</SelectItem>}
                    {isGlobalAdmin && <SelectItem value="center_admin">Admin de centro</SelectItem>}
                    {isGlobalAdmin && <SelectItem value="gerente">Gerente (solo lectura)</SelectItem>}
                    <SelectItem value="recepcionista">Recepcionista</SelectItem>
                    <SelectItem value="audioprotesista">Audioprotesista</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role !== "admin" && (
                <div className="md:col-span-2">
                  <Label>Centro</Label>
                  <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {centers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter className="md:col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="save-user-btn"
                  className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                >
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead>ID</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="border-[#E5E3DB]" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-mono">{u.employee_id}</TableCell>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{ROLES_LABEL[u.role] || u.role}</TableCell>
                <TableCell>{u.center_id ? centerName(u.center_id) : "—"}</TableCell>
                <TableCell>
                  <Badge
                    className={u.active ? "bg-[#D1E0D7] text-[#2A2F2C] cursor-pointer" : "bg-[#E8D1D1] text-[#2A2F2C] cursor-pointer"}
                    onClick={() => u.id !== currentUser?.id && toggleActive(u)}
                  >
                    {u.active ? "Activo" : "Inactivo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 justify-end">
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(u)}
                        data-testid={`edit-user-${u.id}`}
                        title="Editar credenciales y datos"
                      >
                        <Pencil className="h-4 w-4 text-[#4A5D4E]" />
                      </Button>
                    )}
                    {u.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(u.id)}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-[#C07F67]" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Modal de edición */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Editar trabajador</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>ID Trabajador *</Label>
              <Input
                required
                data-testid="edit-user-employee-id"
                value={editForm.employee_id}
                onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value })}
              />
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                required
                data-testid="edit-user-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Nueva contraseña <span className="text-[10px] text-[#6E7772]">(opcional — déjalo vacío para no cambiarla)</span></Label>
              <Input
                type="password"
                data-testid="edit-user-password"
                placeholder="••••••••"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isGlobalAdmin && <SelectItem value="admin">Administrador general (jefe)</SelectItem>}
                  {isGlobalAdmin && <SelectItem value="center_admin">Admin de centro</SelectItem>}
                  {isGlobalAdmin && <SelectItem value="gerente">Gerente (solo lectura)</SelectItem>}
                  <SelectItem value="recepcionista">Recepcionista</SelectItem>
                  <SelectItem value="audioprotesista">Audioprotesista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.role !== "admin" && (
              <div>
                <Label>Centro</Label>
                <Select value={editForm.center_id} onValueChange={(v) => setEditForm({ ...editForm, center_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    {centers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2 border-t border-[#E5E3DB] pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Horario laboral (jornada)</Label>
                <button
                  type="button"
                  className="text-[11px] text-[#4A5D4E] hover:underline"
                  data-testid="copy-monday-btn"
                  onClick={() => {
                    const mon = editSchedule.mon;
                    setEditSchedule((prev) => ({
                      ...prev,
                      tue: { ...mon },
                      wed: { ...mon },
                      thu: { ...mon },
                      fri: { ...mon },
                    }));
                  }}
                >
                  Copiar lunes a Mar–Vie
                </button>
              </div>
              <p className="text-[11px] text-[#6E7772] mb-2">
                Si no marcas ningún día, el trabajador estará disponible siempre. Con horario configurado, en Citas solo se le podrá agendar dentro de su jornada.
              </p>
              <div className="space-y-1.5">
                {WEEK_DAYS.map(([key, label]) => {
                  const d = editSchedule[key];
                  const upd = (patch) =>
                    setEditSchedule((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
                  return (
                    <div key={key} className="flex items-center gap-2 text-sm" data-testid={`schedule-day-${key}`}>
                      <label className="flex items-center gap-1.5 w-24 shrink-0">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5"
                          checked={d.enabled}
                          onChange={(e) => upd({ enabled: e.target.checked })}
                          data-testid={`schedule-enabled-${key}`}
                        />
                        <span className={d.enabled ? "" : "text-[#9CA3AF]"}>{label}</span>
                      </label>
                      {d.enabled ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Input type="time" className="h-8 w-[100px] text-xs" value={d.s1} onChange={(e) => upd({ s1: e.target.value })} data-testid={`schedule-s1-${key}`} />
                          <span className="text-[#6E7772]">–</span>
                          <Input type="time" className="h-8 w-[100px] text-xs" value={d.e1} onChange={(e) => upd({ e1: e.target.value })} data-testid={`schedule-e1-${key}`} />
                          <span className="text-[10px] text-[#6E7772] mx-1">y</span>
                          <Input type="time" className="h-8 w-[100px] text-xs" value={d.s2} onChange={(e) => upd({ s2: e.target.value })} data-testid={`schedule-s2-${key}`} />
                          <span className="text-[#6E7772]">–</span>
                          <Input type="time" className="h-8 w-[100px] text-xs" value={d.e2} onChange={(e) => upd({ e2: e.target.value })} data-testid={`schedule-e2-${key}`} />
                        </div>
                      ) : (
                        <span className="text-xs text-[#9CA3AF] italic">No trabaja</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#6E7772] mt-1.5">
                El segundo tramo es opcional (jornada partida, p. ej. 09:30–14:00 y 16:30–20:00).
              </p>
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button
                type="submit"
                disabled={editSaving}
                data-testid="save-edit-user-btn"
                className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
              >
                {editSaving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
